import React from 'react'
import Link from 'next/link'
import styles from './Action.module.scss'

const ActionContent = ({ icon: Icon, children, wrapContent }) => (
  <>
    {Icon && <Icon aria-hidden='true' />}
    {wrapContent ? <span>{children}</span> : children}
  </>
)

const Action = ({
  children,
  href,
  external = false,
  icon,
  variant = 'primary',
  size = 'standard',
  className = '',
  disabled = false,
  loading = false,
  onClick,
  onFocus,
  onMouseEnter,
  type = 'button',
  id,
  target,
  rel,
  prefetch,
  ariaLabel,
  ariaPressed,
  ariaCurrent,
  ariaExpanded,
  ariaControls,
  ariaHidden,
  tabIndex,
  dataAction,
  dataCatalogId,
  dataConversionChannel,
  dataConversionKey,
  dataConversionLocation,
  dataConversionSalesPath,
  dataProductId,
  title,
  wrapContent = true,
  focusTargetOnNavigate = false
}) => {
  const classes = `${styles.action} ${styles[variant] || ''} ${styles[size] || ''} ${className}`.trim()
  const isDisabled = disabled || loading
  const content = <ActionContent icon={icon} wrapContent={wrapContent}>{children}</ActionContent>
  const handleClick = event => {
    onClick?.(event)

    if (
      !focusTargetOnNavigate ||
      event.defaultPrevented ||
      typeof href !== 'string' ||
      !href.startsWith('#') ||
      typeof document === 'undefined'
    ) return

    window.requestAnimationFrame(() => {
      const section = document.getElementById(decodeURIComponent(href.slice(1)))
      const target = section?.querySelector('[data-section-focus-target], h1, h2, h3') || section
      if (!target) return

      const hadTabIndex = target.hasAttribute('tabindex')
      if (!hadTabIndex) target.setAttribute('tabindex', '-1')
      target.focus({ preventScroll: true })

      if (!hadTabIndex) {
        target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true })
      }
    })
  }
  const accessibilityProps = {
    'data-msr-action': variant,
    'aria-busy': loading || undefined,
    'aria-controls': ariaControls,
    'aria-current': ariaCurrent,
    'aria-expanded': ariaExpanded,
    'aria-hidden': ariaHidden,
    'aria-label': ariaLabel,
    'aria-pressed': ariaPressed,
    'data-action': dataAction,
    'data-catalog-id': dataCatalogId,
    'data-conversion-channel': dataConversionChannel,
    'data-conversion-key': dataConversionKey,
    'data-conversion-location': dataConversionLocation,
    'data-conversion-sales-path': dataConversionSalesPath,
    'data-product-id': dataProductId,
    id,
    onFocus,
    onMouseEnter,
    tabIndex,
    title
  }

  if (href && external) {
    if (isDisabled) {
      return (
        <span className={classes} role='link' aria-disabled='true' {...accessibilityProps}>
          {content}
        </span>
      )
    }

    return (
      <a
        className={classes}
        href={href}
        target={target}
        rel={rel || (target === '_blank' ? 'noreferrer noopener' : undefined)}
        onClick={handleClick}
        {...accessibilityProps}
      >
        {content}
      </a>
    )
  }

  if (href) {
    if (isDisabled) {
      return (
        <span className={classes} role='link' aria-disabled='true' {...accessibilityProps}>
          {content}
        </span>
      )
    }

    return (
      <Link
        href={href}
        legacyBehavior
        {...(prefetch === false ? { prefetch: false } : {})}
      >
        <a
          className={classes}
          onClick={handleClick}
          target={target}
          rel={rel}
          {...accessibilityProps}
        >
          {content}
        </a>
      </Link>
    )
  }

  return (
    <button
      className={classes}
      type={type}
      disabled={isDisabled}
      onClick={handleClick}
      {...accessibilityProps}
    >
      {content}
    </button>
  )
}

export default Action
